import {
  setIsOverlayLogin,
  useApp,
  useAppDispatch,
  useEvent,
  UserRoles,
  useTranslation,
  useUserInfo,
  useUserPlanName,
} from "@next-platform/client-common-core";
import {
  Avatar,
  AvatarSize,
  Button,
  ButtonIcon,
  ButtonSize,
  ButtonVariant,
  IconVariants,
  Shape,
  Text,
  TextVariants,
  useMedia,
} from "@next-platform/client-common-spicy-ui";
import { memo } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { SiteLogo } from "@/components";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import { BLACKLISTED_APP_HEADER_ROUTES, GET_PREMIUM_EVENT_NAME, HEADER_HEIGHT, ROUTES } from "@/constants";
import { Theme } from "@/constants/theme";
import { useMediaQuery } from "@/hooks";
import useIsMultiLangEnabled from "@/hooks/useIsMultiLangEnabled";
import useNavigation from "@/hooks/useNavigation";
import { useTheme } from "@/hooks/useTheme";
import useAuth from "@/services/useAuth";
import { AvatarPreset } from "@/types";
import { cn, getAvatarUrl } from "@/utils";
import { getIsPartner } from "@/utils/getIsPartner";

import AnnouncekitNotification from "../AnnouncekitNotification";
import LocaleSelector from "../LocaleSelector/LocaleSelector";
import Trans from "../Trans/Trans";

function NavigationHeader() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { sendEvent } = useEvent();
  const { isDesktop, isMobile, isTablet } = useMedia();
  const isSmall = useMediaQuery(425);
  const { isAuthenticated: isUserAuthenticated } = useAuth();
  const { isDark, setTheme } = useTheme();
  const { name, avatar_url, isProfileModified, username, role, onlyUserId, guestUserId } = useUserInfo();
  const { isSideNavOpen, checkRoute, handleSideNavToggle } = useNavigation();
  const { pathname } = useLocation();
  const { appSettings } = useApp();
  const { t } = useTranslation();
  const isPartner = getIsPartner();
  const { isMultiLangEnabled } = useIsMultiLangEnabled();
  const isAuthenticated = !!(username || (isPartner && isUserAuthenticated));
  const shouldShowSubscriptionTier = !isPartner && !isMobile;
  const isFreeUser = role === UserRoles.FREE;
  const isAnonymousOrFreeUser = !isAuthenticated || isFreeUser;
  const showGetPremiumButton = !isPartner && isAnonymousOrFreeUser;
  const navHeaderButton = appSettings?.navHeaderButton;
  const planName = useUserPlanName(role);
  const planLabel = isFreeUser ? t("common:free") : planName;

  const handleTheme = () => {
    setTheme(isDark ? Theme.LIGHT : Theme.DARK);
  };

  const onClickLogin = () => {
    dispatch(setIsOverlayLogin(true));
  };

  const isBlacklistedRoute = (pathname: string): boolean => {
    return BLACKLISTED_APP_HEADER_ROUTES.some((route) => new RegExp(route).test(pathname));
  };

  const handleSideNavClick = () => {
    handleSideNavToggle(!isSideNavOpen);
  };

  const handleGetPremium = () => {
    const eventData: Record<string, string> = isAuthenticated ? { user_id: onlyUserId || "" } : { guest_user_id: guestUserId || "" };
    sendEvent({ event_name: GET_PREMIUM_EVENT_NAME, data: eventData });
    if (navHeaderButton?.link) {
      const buttonUrl = navHeaderButton.link.trim();
      if (buttonUrl.startsWith("https://")) {
        window.open(buttonUrl, "_blank", "noreferrer");
      } else {
        navigate(buttonUrl);
      }
    } else {
      navigate(ROUTES.subscribe);
    }
  };

  const hideHeaderInChat = (isMobile || isTablet) && isBlacklistedRoute(pathname);

  return (
    <div
      className={`py-2.5 px-[18px] flex items-center bg-white dark:bg-gradient-to-b from-blue-1 to-black gap-2.5 max-mob:p-2.5 sticky top-0 z-[100] ${hideHeaderInChat ? "hidden" : ""}`}
      style={{ height: HEADER_HEIGHT }}
    >
      {(!isSideNavOpen || !isDesktop) && (
        <div className="flex items-center">
          {!isDesktop && (
            <ButtonIcon
              icon={IconVariants.MENU}
              size={ButtonSize.MEDIUM}
              onClick={handleSideNavClick}
              variant={ButtonVariant.TEXT}
              aria-label="menu"
            />
          )}
          {(!showGetPremiumButton || !isMultiLangEnabled || !isMobile) && (
            <LocalizedLink onClick={(e) => checkRoute(e, "/")} to={isProfileModified ? "#" : "/"} aria-label="home">
              <SiteLogo type={isAuthenticated && isMultiLangEnabled && isSmall ? "icon" : "logo"} className="h-[22px]" />
            </LocalizedLink>
          )}
          {showGetPremiumButton && isMultiLangEnabled && isMobile ? (
            <Button
              variant={ButtonVariant.TRANSPARENT}
              shape={Shape.PILL}
              iconStart={(navHeaderButton?.icon as IconVariants) || IconVariants.GEM}
              className="bg-gradient-to-r from-purple-9 to-blue-9 text-white"
              onClick={handleGetPremium}
              data-testid="GetPremiumButton"
            >
              {isMobile ? navHeaderButton?.label || <Trans i18nKey="common:getPremiumLabel" /> : ""}
            </Button>
          ) : null}
        </div>
      )}

      <div className="flex items-center gap-2.5 ml-auto">
        {(!isMultiLangEnabled && showGetPremiumButton) || (showGetPremiumButton && !isMobile) ? (
          <Button
            variant={ButtonVariant.TRANSPARENT}
            shape={Shape.PILL}
            iconStart={(navHeaderButton?.icon as IconVariants) || IconVariants.GEM}
            className="bg-gradient-to-r from-purple-9 to-blue-9 text-white"
            onClick={handleGetPremium}
            data-testid="GetPremiumButton"
          >
            {!isMobile ? navHeaderButton?.label || <Trans i18nKey="common:getPremiumLabel" /> : ""}
          </Button>
        ) : null}
        {isMultiLangEnabled ? <LocaleSelector /> : null}
        {isAuthenticated ? <AnnouncekitNotification /> : null}

        <ButtonIcon
          icon={isDark ? IconVariants.MOON : IconVariants.SUN}
          size={ButtonSize.MEDIUM}
          shape={Shape.SQUARE}
          onClick={handleTheme}
          variant={ButtonVariant.TEXT}
          aria-label="theme"
        />

        {isAuthenticated ? (
          <LocalizedLink
            className={cn(
              "flex items-center gap-sm",
              shouldShowSubscriptionTier && "border border-solid border-gray-5 dark:border-gray-8 rounded-full p-1 pr-[13px]",
            )}
            onClick={(e) => checkRoute(e, ROUTES.profile)}
            to={isProfileModified ? "#" : ROUTES.profile}
            aria-label="avatar"
          >
            {avatar_url ? (
              <Avatar
                src={getAvatarUrl(avatar_url, { preset: AvatarPreset.AVATAR_48X48 }) as string}
                name={name?.[0]}
                classNames={{
                  name: "!text-heading-6 capitalize",
                }}
                size={shouldShowSubscriptionTier ? AvatarSize.SMALL : AvatarSize.MEDIUM}
              />
            ) : (
              <div className={cn("size-9 rounded-full bg-purple-9 grid place-items-center capitalize", shouldShowSubscriptionTier && "size-7")}>
                <Text variant={TextVariants.HEADING_6_REGULAR} className="text-white">
                  {name?.[0]}
                </Text>
              </div>
            )}
            {shouldShowSubscriptionTier ? <Text variant={TextVariants.LABEL_MEDIUM_MEDIUM}>{planLabel}</Text> : null}
          </LocalizedLink>
        ) : isMobile ? (
          <ButtonIcon
            className={"!bg-black dark:!bg-white !text-primary-background !border-0"}
            icon={IconVariants.LOGIN}
            shape={Shape.SQUARE}
            variant={ButtonVariant.TRANSPARENT}
            size={ButtonSize.MEDIUM}
            onClick={onClickLogin}
            aria-label="login"
          />
        ) : (
          <Button
            className="!bg-black !text-primary-background dark:!bg-white"
            iconStart={IconVariants.LOGIN}
            variant={ButtonVariant.TRANSPARENT}
            onClick={onClickLogin}
            aria-label="login"
          >
            {!isMobile && (
              <Text as="span" variant={TextVariants.LABEL_MEDIUM_REGULAR}>
                <Trans i18nKey="common:signIn" />
              </Text>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}

export default memo(NavigationHeader);
